import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, TrendingUp, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

/**
 * Per-month plan usage meter. Two progress bars (issued / received) with
 * green→amber→rose color steps and a CTA to upgrade when approaching the cap.
 *
 * Props:
 *  - compact: render a slim variant suitable for embedding in banners.
 */
export default function UsageMeter({ compact = false }) {
  const { lang } = useI18n();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/billing/usage");
        setUsage(r.data);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  if (!usage) return null;
  if (usage.unlimited) {
    // Unlimited plan or no subscription — show a slim badge for visibility
    return (
      <div className={`flex items-center gap-2 text-xs text-emerald-200 ${compact ? "" : "card-surface p-3"}`}
           data-testid="usage-meter-unlimited">
        <TrendingUp size={12} />
        <span>
          {lang === "es"
            ? "Uso ilimitado"
            : "Ús il·limitat"}
          {usage.plan && (
            <span className="opacity-60 ml-1">· {usage.plan}</span>
          )}
        </span>
      </div>
    );
  }

  const wrapperCls = compact
    ? "rounded-lg border border-white/10 bg-white/[0.02] p-3"
    : "card-surface p-4 space-y-3";

  return (
    <div className={wrapperCls} data-testid="usage-meter">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400">
          <Activity size={12} />
          {lang === "es" ? "Uso este mes" : "Ús aquest mes"}
          <span className="font-mono text-slate-500 normal-case">
            · {usage.period_label}
          </span>
        </div>
        {usage.plan && (
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            {usage.plan}
          </span>
        )}
      </div>
      <UsageBar
        label={lang === "es" ? "Emitidas" : "Emeses"}
        entry={usage.issued}
        testid="usage-issued"
      />
      <UsageBar
        label={lang === "es" ? "Recibidas" : "Rebudes"}
        entry={usage.received}
        testid="usage-received"
      />
      {(usage.issued.approaching || usage.issued.reached
        || usage.received.approaching || usage.received.reached) && (
        <Link
          to="/app/pricing"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-200 hover:text-amber-100"
          data-testid="usage-upgrade-link"
        >
          <AlertTriangle size={12} />
          {lang === "es"
            ? "Cerca del límite — mejora a Pro"
            : "A prop del límit — millora a Pro"}
        </Link>
      )}
    </div>
  );
}

function UsageBar({ label, entry, testid }) {
  const pct = entry.percent ?? 0;
  const color = entry.reached
    ? "bg-rose-400"
    : entry.approaching
    ? "bg-amber-400"
    : "bg-emerald-400";
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-300 font-medium">{label}</span>
        <span className="text-slate-400 tabular-nums font-mono">
          {entry.used} / {entry.limit}
        </span>
      </div>
      <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}
